import React, { useId } from "react";
import {
  Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, Line, LineChart, Pie, PieChart,
  ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import { BarChart3 } from "lucide-react";
import { useChipMonkTheme } from "../theme/ThemeProvider";
import { EmptyState, Skeleton } from "./States";
import { cx, formatINRCompact, formatNumber } from "./format";

/**
 * Chart kit. Rules (see the ChipMonk design system):
 *  - series take colours in a fixed order (gold, blue, teal, red, violet, amber);
 *    anything past six folds into "Other"
 *  - one y-axis per chart, faint grid, hover tooltip on every mark
 *  - money in/out always uses `positive` (teal) / `negative` (red)
 *  - ≥2 series always show a legend; a single series is named by the card title
 */
export const useChartColors = () => {
  const { colors } = useChipMonkTheme();
  return colors;
};

export const seriesColor = (colors, i) => colors.chart[i % colors.chart.length];

export const ChartLegend = ({ items = [], className }) => (
  <div className={cx("flex flex-wrap gap-x-4 gap-y-1 text-xs text-cm-muted", className)}>
    {items.map((it) => (
      <span key={it.label} className="inline-flex items-center gap-1.5">
        <i className="w-2.5 h-2.5 rounded-[3px] inline-block" style={{ background: it.color }} />
        {it.label}
      </span>
    ))}
  </div>
);

/** Card that frames a chart: the title is the question the chart answers. */
export const ChartCard = ({
  title, description, legend, actions, loading, empty, emptyText = "No data for this period yet.",
  height = 260, className, children,
}) => (
  <section className={cx("bg-cm-surface border border-cm-border rounded-cm-lg p-4 sm:p-5 min-w-0", className)}>
    <header className="flex flex-wrap items-start justify-between gap-3 mb-3">
      <div className="min-w-0">
        {title && <h3 className="text-base font-bold text-cm-text m-0">{title}</h3>}
        {description && <p className="text-[13px] text-cm-muted m-0 mt-0.5">{description}</p>}
      </div>
      <div className="flex flex-wrap items-center gap-3">
        {legend && <ChartLegend items={legend} />}
        {actions}
      </div>
    </header>
    <div style={height === "auto" ? undefined : { height }} className="w-full min-w-0">
      {loading ? (
        <Skeleton className="w-full h-full min-h-[200px]" />
      ) : empty ? (
        <div className="h-full min-h-[200px] grid place-items-center">
          <EmptyState compact icon={BarChart3} title="Nothing to chart yet" description={emptyText} />
        </div>
      ) : (
        children
      )}
    </div>
  </section>
);

/** Themed tooltip used by every chart. */
export const ChartTooltip = ({ active, payload, label, valueFormat = formatNumber, labelFormat }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="rounded-cm border border-cm-border bg-cm-surface-3 shadow-cm-md px-3 py-2 text-xs min-w-[140px]">
      {label !== undefined && label !== "" && (
        <div className="font-semibold text-cm-text mb-1">{labelFormat ? labelFormat(label) : label}</div>
      )}
      {payload.map((p) => (
        <div key={p.dataKey || p.name} className="flex items-center justify-between gap-4 py-0.5">
          <span className="flex items-center gap-1.5 text-cm-muted">
            <i className="w-2 h-2 rounded-sm inline-block" style={{ background: p.color || p.payload?.fill }} />
            {p.name}
          </span>
          <span className="font-semibold text-cm-text tabular-nums">{valueFormat(p.value, p)}</span>
        </div>
      ))}
    </div>
  );
};

const axisProps = (colors) => ({
  stroke: colors.axis,
  tick: { fill: colors.axis, fontSize: 11, fontFamily: "Inter, system-ui, sans-serif" },
  tickLine: false,
  axisLine: false,
});

const moneyOrNumber = (money) => (money ? formatINRCompact : (v) => formatNumber(v));

/** One unit for every tick on a money axis (₹k, ₹L or ₹Cr), picked from the largest value. */
const moneyAxis = (data, keys) => {
  const max = Math.max(0, ...data.flatMap((row) => keys.map((k) => Math.abs(Number(row[k]) || 0))));
  const [div, unit] = max >= 1e7 ? [1e7, "Cr"] : max >= 1e5 ? [1e5, "L"] : max >= 1e3 ? [1e3, "k"] : [1, ""];
  return (v) => {
    if (!v) return "₹0";
    const n = v / div;
    return `₹${Number.isInteger(n) ? n : n.toFixed(n < 10 ? 1 : 0).replace(/.0$/, "")}${unit}`;
  };
};

/**
 * Line / area trend over time.
 *   <TrendChart data={rows} xKey="date" series={[{ key: "rake", name: "Rake" }]} money area />
 */
export const TrendChart = ({
  data = [], xKey = "label", series = [], money = false, area = true, height = "100%",
  yFormat, tooltipFormat, xFormat, showDots = false,
}) => {
  const colors = useChartColors();
  const yf = yFormat || (money ? moneyAxis(data, series.map((x) => x.key)) : moneyOrNumber(false));
  const tf = tooltipFormat || (money ? (v) => `₹${Number(v || 0).toLocaleString("en-IN")}` : (v) => formatNumber(v));
  const Chart = area ? AreaChart : LineChart;
  const gid = useId().replace(/:/g, "");
  return (
    <ResponsiveContainer width="100%" height={height}>
      <Chart data={data} margin={{ top: 8, right: 12, left: 4, bottom: 0 }}>
        <defs>
          {series.map((s, i) => {
            const c = s.color || seriesColor(colors, i);
            return (
              <linearGradient key={s.key} id={`cm-grad-${gid}-${s.key}`} x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor={c} stopOpacity={0.28} />
                <stop offset="100%" stopColor={c} stopOpacity={0} />
              </linearGradient>
            );
          })}
        </defs>
        <CartesianGrid stroke={colors.grid} vertical={false} />
        <XAxis dataKey={xKey} {...axisProps(colors)} tickFormatter={xFormat} minTickGap={16} />
        <YAxis {...axisProps(colors)} tickFormatter={yf} width={money ? 56 : 44} />
        <Tooltip
          cursor={{ stroke: colors.borderStrong, strokeDasharray: "3 3" }}
          content={<ChartTooltip valueFormat={tf} labelFormat={xFormat} />}
        />
        {series.map((s, i) => {
          const c = s.color || seriesColor(colors, i);
          return area ? (
            <Area
              key={s.key}
              type="monotone"
              dataKey={s.key}
              name={s.name || s.key}
              stroke={c}
              strokeWidth={2}
              fill={s.fill === false ? "transparent" : `url(#cm-grad-${gid}-${s.key})`}
              dot={showDots ? { r: 3, strokeWidth: 0, fill: c } : false}
              activeDot={{ r: 5, stroke: colors.surface, strokeWidth: 2 }}
              isAnimationActive={false}
            />
          ) : (
            <Line
              key={s.key}
              type="monotone"
              dataKey={s.key}
              name={s.name || s.key}
              stroke={c}
              strokeWidth={2}
              dot={showDots ? { r: 3, strokeWidth: 0, fill: c } : false}
              activeDot={{ r: 5, stroke: colors.surface, strokeWidth: 2 }}
              isAnimationActive={false}
            />
          );
        })}
      </Chart>
    </ResponsiveContainer>
  );
};

/**
 * Bars. Vertical by default; `horizontal` for ranked lists (long labels).
 * `stacked` stacks series. Money in/out: pass colors from useChartColors().positive/negative.
 */
export const BarsChart = ({
  data = [], xKey = "label", series = [], money = false, stacked = false, horizontal = false,
  height = "100%", yFormat, tooltipFormat, xFormat, barSize,
}) => {
  const colors = useChartColors();
  const vf = yFormat || (money ? moneyAxis(data, series.map((x) => x.key)) : moneyOrNumber(false));
  const tf = tooltipFormat || (money ? (v) => `₹${Number(v || 0).toLocaleString("en-IN")}` : (v) => formatNumber(v));
  const radius = horizontal ? [0, 4, 4, 0] : [4, 4, 0, 0];
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart
        data={data}
        layout={horizontal ? "vertical" : "horizontal"}
        margin={{ top: 8, right: 12, left: horizontal ? 8 : 4, bottom: 0 }}
        barGap={2}
        barCategoryGap={horizontal ? "22%" : "28%"}
      >
        <CartesianGrid stroke={colors.grid} vertical={horizontal} horizontal={!horizontal} />
        {horizontal ? (
          <>
            <XAxis type="number" {...axisProps(colors)} tickFormatter={vf} />
            <YAxis type="category" dataKey={xKey} {...axisProps(colors)} width={120} tickFormatter={xFormat} />
          </>
        ) : (
          <>
            <XAxis dataKey={xKey} {...axisProps(colors)} tickFormatter={xFormat} minTickGap={8} />
            <YAxis {...axisProps(colors)} tickFormatter={vf} width={money ? 56 : 44} />
          </>
        )}
        <Tooltip cursor={{ fill: colors.surface2, opacity: 0.6 }} content={<ChartTooltip valueFormat={tf} labelFormat={xFormat} />} />
        {series.map((s, i) => (
          <Bar
            key={s.key}
            dataKey={s.key}
            name={s.name || s.key}
            fill={s.color || seriesColor(colors, i)}
            stackId={stacked ? "stack" : undefined}
            radius={stacked && i < series.length - 1 ? 0 : radius}
            maxBarSize={barSize || 28}
            stroke={stacked ? colors.surface : undefined}
            strokeWidth={stacked ? 1 : 0}
            isAnimationActive={false}
          >
            {s.cellColors && data.map((row, j) => <Cell key={j} fill={s.cellColors(row, j)} />)}
          </Bar>
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
};

/**
 * Share of a whole. Keep to ≤ 4 slices (3 colours + "Other" in neutral);
 * the legend beside it lists value and %.
 */
export const DonutChart = ({ data = [], money = false, centerLabel, height = 200, maxSlices = 4 }) => {
  const colors = useChartColors();
  const sorted = [...data].filter((d) => Number(d.value) > 0).sort((a, b) => b.value - a.value);
  let slices = sorted;
  if (sorted.length > maxSlices) {
    const head = sorted.slice(0, maxSlices - 1);
    const rest = sorted.slice(maxSlices - 1).reduce((a, d) => a + Number(d.value), 0);
    slices = [...head, { name: "Other", value: rest, other: true }];
  }
  const total = slices.reduce((a, d) => a + Number(d.value), 0);
  const fmt = money ? formatINRCompact : (v) => formatNumber(v);
  const colorOf = (d, i) => (d.other ? colors.neutral : d.color || seriesColor(colors, i));
  return (
    <div className="flex flex-wrap items-center justify-center gap-5 h-full">
      <div className="relative shrink-0" style={{ height, width: height }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={slices}
              dataKey="value"
              nameKey="name"
              innerRadius="64%"
              outerRadius="96%"
              paddingAngle={0}
              stroke={colors.surface}
              strokeWidth={2}
              startAngle={90}
              endAngle={-270}
              isAnimationActive={false}
            >
              {slices.map((d, i) => <Cell key={d.name} fill={colorOf(d, i)} />)}
            </Pie>
            <Tooltip
              content={
                <ChartTooltip
                  valueFormat={(v) => `${money ? `₹${Number(v).toLocaleString("en-IN")}` : formatNumber(v)} · ${total ? Math.round((v / total) * 100) : 0}%`}
                />
              }
            />
          </PieChart>
        </ResponsiveContainer>
        <div className="absolute inset-0 grid place-items-center pointer-events-none text-center">
          <div>
            <div className="font-display font-extrabold text-lg text-cm-text tabular-nums">{fmt(total)}</div>
            {centerLabel && <div className="text-[11px] text-cm-muted">{centerLabel}</div>}
          </div>
        </div>
      </div>
      <ul className="flex flex-col gap-2.5 m-0 p-0 list-none text-[13px] flex-1 min-w-[190px]">
        {slices.map((d, i) => (
          <li key={d.name} className="grid grid-cols-[12px_minmax(0,1fr)_auto_auto] gap-2.5 items-center">
            <i className="w-3 h-3 rounded-[3px]" style={{ background: colorOf(d, i) }} />
            <span className="truncate text-cm-text">{d.name}</span>
            <span className="font-semibold tabular-nums text-cm-text">{fmt(d.value)}</span>
            <span className="text-cm-muted tabular-nums w-10 text-right">{total ? Math.round((d.value / total) * 100) : 0}%</span>
          </li>
        ))}
      </ul>
    </div>
  );
};
